import useMountEffect from '@restart/hooks/useMountEffect';
import { useMemo } from 'react';
import { useDispatch, useStore } from 'react-redux';

import actions from '@/actions';
import EventEmitter from '@/libs/EventEmitter';
import { convertFloat32ToInt32 } from '@/utils/BinaryUtils';
import { isColdPosition } from '@/utils/MapUtils';

const useMavLinkTelemetryHandler = (robot, allowedMsgIds) => {
  const dispatch = useDispatch();
  const store = useStore();

  const isPX4 = useMemo(() => {
    return robot.model.maker.includes('PX4');
  }, []);

  useMountEffect(() => {
    const subscribeToken = EventEmitter.subscribe(`${robot.id}/telemetry/mavlink`, clarifyMessage);

    return () => {
      EventEmitter.unsubscribe(subscribeToken);
    };
  });

  const clarifyMessage = ({ header, payload }) => {
    const msgId = header.msgid;
    const sysId = header.sysid;
    const compId = header.compid;

    // 허용된 MsgId 아닌 경우 처리 제외
    if (allowedMsgIds && !allowedMsgIds.includes(msgId)) return;
    // 로봇별 SysId, CompId 저장
    updateTelemetry('address', { sysId, compId });

    switch (msgId) {
      case 0: // HEARTBEAT
        updateTelemetry(getKey(msgId), {
          isArm: payload.baseMode >= 128,
          mode: payload.customMode,
          status: payload.systemStatus,
        });
        break;

      case 22: // PARAM_VALUE
        if (payload.paramId === '_HASH_CHECK') return;

        publishTelemetry(getKey(msgId), payload);
        updateRobotParams(payload);
        break;

      case 24: // GPS_RAW_INT
      case 30: // ATTITUDE
      case 74: // VFR_HUD
      case 148: // AUTOPILOT_VERSION
      case 193: // EKF_STATUS_REPORT
      case 241: // VIBRATION
        updateTelemetry(getKey(msgId), payload);
        break;

      case 33: // GLOBAL_POSITION_INT
        {
          const lat = payload.lat / 1e7;
          const lng = payload.lon / 1e7;

          // GPS 데이터 유효하지 않은 경우
          if (isColdPosition(lat, lng)) return;

          updateTelemetry(getKey(msgId), {
            lat,
            lng,
            alt: payload.alt / 1000,
            ralt: payload.relativeAlt / 1000,
            hdg: payload.hdg / 100,
          });

          EventEmitter.publish('position/update', {
            robotId: robot.id,
            position: { lat, lng },
          });
        }
        break;

      case 42: // MISSION_CURRENT
        {
          const data = { ...payload };
          // TODO: PX4 조건 상수화
          if (robot.model.maker === 'PX4') {
            data.seq++;
          }
          updateTelemetry(getKey(msgId), data);
        }
        break;

      case 40: // MISSION_REQUEST
      case 44: // MISSION_COUNT
      case 47: // MISSION_ACK
      case 51: // MISSION_REQUEST_INT
      case 73: // MISSION_ITEM_INT
      case 76: // COMMAND_LONG
      case 77: // COMMAND_ACK
      case 191: // MAG_CAL_PROGRESS
      case 192: // MAG_CAL_REPORT
        publishTelemetry(getKey(msgId), payload);
        break;

      case 147: // BATTERY_STATUS
        updateTelemetry(getKey(msgId), { voltage: payload.voltages[0] });
        break;

      case 180: // CAMERA_FEEDBACK
        updateTelemetry(getKey(msgId), {
          imgIdx: payload.imgIdx,
          lat: payload.lat / 1e7,
          lng: payload.lng / 1e7,
        });
        break;

      case 242: // HOME_POSITION
        {
          const lat = payload.latitude / 1e7;
          const lng = payload.longitude / 1e7;
          const homePosition = store.getState().telemetry[robot.id]?.homePosition;

          // 변경 감지된 경우에 한해 업데이트
          if (!homePosition || homePosition.lat !== lat || homePosition.lng !== lng) {
            updateTelemetry(getKey(msgId), { lat, lng });
          }
        }
        break;

      case 245: // EXTENDED_SYS_STATE
        updateTelemetry(getKey(msgId), {
          isFixedWing: [1, 4].includes(payload.vtolState),
          isMulticopter: [2, 3].includes(payload.vtolState),
          landedState: payload.landedState,
        });
        break;

      case 253: // STATUSTEXT
        if (payload) {
          dispatch(actions.notifications.add(robot.id, payload.text));
          publishTelemetry(getKey(msgId), payload);
        }
        break;

      default:
        break;
    }
  };

  const getKey = (msgId) => {
    // prettier-ignore
    switch (msgId) {
      case 0: return 'heartbeat';
      case 22: return 'paramValue';
      case 24: return 'gpsRaw';
      case 30: return 'attitude';
      case 33: return 'globalPosition';
      case 40: return 'missionRequest';
      case 42: return 'missionCurrent';
      case 44: return 'missionCount';
      case 47: return 'missionAck';
      case 51: return 'missionRequestInt';
      case 73: return 'missionItem';
      case 74: return 'vfrHud';
      case 76: return 'commandLong';
      case 77: return 'commandAck';
      case 147: return 'batteryStatus';
      case 148: return 'autopilotVersion';
      case 180: return 'cameraFeedback';
      case 191: return 'magCalProgress';
      case 192: return 'magCalReport';
      case 193: return 'ekfStatusReport';
      case 241: return 'vibration';
      case 242: return 'homePosition';
      case 245: return 'extendedSysState';
      case 253: return 'statustext';
      default: return;
    }
  };

  const updateRobotParams = (payload) => {
    const params = { ...payload };

    // 6: 32-bit signed integer
    if (payload.paramType === 6 && isPX4) {
      // int32 변환
      params.paramValue = convertFloat32ToInt32(payload.paramValue);
    }
    // 9: 32-bit floating-point
    else if (payload.paramType === 9) {
      // JavaScript 부동소수점 연산 오차 방지 위한 4자리 제한
      params.paramValue = Number(payload.paramValue.toFixed(4));
    }

    dispatch(actions.robotParams.set(robot.id, params));
  };

  const updateTelemetry = (key, data) => {
    dispatch(actions.telemetry.set(robot.id, key, data));
  };

  const publishTelemetry = (key, data) => {
    EventEmitter.publish(`${robot.id}/telemetry/${key}`, data);
  };
};

export default useMavLinkTelemetryHandler;
